import React, { useState, useMemo, useEffect } from 'react';
import { FormControl, InputLabel, Select, InputAdornment, TextField } from '@mui/material';
import { UserType } from '../../../common/commonTypes';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import LoadingButton from '@mui/lab/LoadingButton';
import MenuItem from '@mui/material/MenuItem';
import { Trans, useTranslation } from 'react-i18next';
import { saveSettings } from '../../../api';

import { FormControlLabel, FormLabel, Radio, RadioGroup, Slider } from '@mui/material';
import {
    Gender,
    ActivityLevelKey,
    GoalKey,
    DietKey,
    calculateBMR,
    getActivityFactor,
    adjustCaloriesForGoal,
    getDietProfile,
    calculateMacros,
} from './helpers';
import MacroBar from '../../MacroBar';
import MacroBullet from '../../MacroBullet';
import DietTypeInfoPopover from './DietTypeInfoPopover';

const activityMarks: ActivityLevelKey[] = ['minimal', 'light', 'moderate', 'active', 'very_active'];

const goalOptions: string[] = ['lose', 'maintain', 'gain'];

const dietOptions: string[] = [
    'classic_keto',
    'high_protein_keto',
    'mediterranean',
    'high_protein_low_carb',
    'zone',
    'modern_paleo',
];

type ModalCalculatorType = {
    user: UserType;
    onCancel: () => void;
    onSuccess: () => void;
};

const CalculatorModal = (props: ModalCalculatorType) => {
    const { user, onCancel, onSuccess } = props;

    const { t, i18n } = useTranslation();

    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const [gender, setGender] = useState<Gender>('female');
    const [age, setAge] = useState<string>('25'); // string to allow empty fields
    const [weight, setWeight] = useState<string>('65'); // string to allow empty fields
    const [height, setHeight] = useState<string>('165'); // string to allow empty fields
    const [activityIndex, setActivityIndex] = useState<number>(2); // Moderate
    const [goal, setGoal] = useState<GoalKey>('maintain');
    const [diet, setDiet] = useState<DietKey>('zone');

    const [ageError, setAgeError] = useState<boolean>(false);
    const [weightError, setWeightError] = useState<boolean>(false);
    const [heightError, setHeightError] = useState<boolean>(false);

    // for info popovers
    const [dietTypeInfoAnchorEl, setDietTypeInfoAnchorEl] =
        React.useState<HTMLButtonElement | null>(null);

    // for info popovers
    const handleDietTypeInfoClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setDietTypeInfoAnchorEl(event.currentTarget);
    };
    const handleDietTypeInfoClose = () => {
        setDietTypeInfoAnchorEl(null);
    };

    useEffect(() => {
        setAgeError(Number(age) < 18 || Number(age) > 100);
        setWeightError(Number(weight) < 30 || Number(weight) > 230);
        setHeightError(Number(height) < 120 || Number(height) > 230);
    }, [age, weight, height]);

    const results = useMemo(() => {
        if (ageError || weightError || heightError) {
            return {
                calories: 0,
                protein: 0,
                fats: 0,
                carbsNoSugars: 0,
                sugars: 0,
            };
        }

        const bmr = calculateBMR({
            gender,
            weightKg: Number(weight),
            heightCm: Number(height),
            ageYears: Number(age),
        });
        const factor = getActivityFactor(activityMarks[activityIndex]);
        const tdee = bmr * factor;
        const targetCalories = adjustCaloriesForGoal(tdee, goal);
        const profile = getDietProfile(diet);
        return calculateMacros(targetCalories, profile);
    }, [
        gender,
        age,
        weight,
        height,
        activityIndex,
        goal,
        diet,
        ageError,
        weightError,
        heightError,
    ]);

    const handleSave = async () => {
        setLoading(true);
        setErrorMessage('');
        try {
            // update on BE
            await saveSettings(user.id, {
                goals: results,
            });

            // update in place
            user.settings = {
                ...user.settings,
                goals: results,
            };

            window.gtag?.('event', `Saved Calculator Results`, {});

            setLoading(false);
            onSuccess();
        } catch (error: any) {
            console.log('%c Error saving settings >>> ', 'background: #333; color: #bada55', error);

            const message =
                error.response.data.message ||
                error.response.data.error ||
                `Can't save goals, please try again later.`;
            setErrorMessage(message);
            setLoading(false);
        }
    };

    return (
        <Dialog
            fullWidth
            open
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    onCancel();
                }
            }}
        >
            {/*<DialogTitle>{t('DRAWER_BTN_calculator')}</DialogTitle>*/}
            <DialogTitle>
                {t(
                    !user.settings.goals.calories || user.settings.goals.protein === 0
                        ? 'CALC_MODAL_INIT_TITLE'
                        : 'DRAWER_BTN_calculator'
                )}
            </DialogTitle>
            <DialogContent>
                {Boolean(errorMessage) && (
                    <Alert severity="error" className="mb-4">
                        {errorMessage}
                    </Alert>
                )}

                {/* Line 1 */}
                <div className="flex mt-4 justify-between">
                    <FormControl style={{ marginTop: '-10px' }}>
                        <FormLabel>{t('gender')}</FormLabel>
                        <RadioGroup
                            row
                            value={gender}
                            onChange={(e) => setGender(e.target.value as Gender)}
                        >
                            <FormControlLabel
                                value="female"
                                control={<Radio />}
                                label={t('gender_F')}
                            />
                            <FormControlLabel
                                value="male"
                                control={<Radio />}
                                label={t('gender_M')}
                            />
                        </RadioGroup>
                    </FormControl>

                    <TextField
                        style={{ width: '48%' }}
                        type="number"
                        label={t('age')}
                        value={age}
                        error={ageError}
                        helperText={ageError ? t('age_error_msg') : ''}
                        onChange={(e) => setAge(e.target.value)}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">{t('years')}</InputAdornment>
                                ),
                            },
                        }}
                    />
                </div>

                {/* Line 2 */}
                <div className="flex mt-4 justify-between">
                    <TextField
                        style={{ width: '48%' }}
                        type="number"
                        label={t('weight')}
                        value={weight}
                        error={weightError}
                        helperText={weightError ? t('weight_error_msg') : ''}
                        onChange={(e) => setWeight(e.target.value)}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">{t('kg')}</InputAdornment>
                                ),
                            },
                        }}
                    />

                    <TextField
                        style={{ width: '48%' }}
                        type="number"
                        label={t('height')}
                        value={height}
                        error={heightError}
                        helperText={heightError ? t('height_error_msg') : ''}
                        onChange={(e) => setHeight(e.target.value)}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">{t('cm')}</InputAdornment>
                                ),
                            },
                        }}
                    />
                </div>

                {/* Line 3 */}
                <div className="mt-4 p-4 border border-gray-300 rounded">
                    <FormLabel>{t('activity_level')}</FormLabel>
                    <Slider
                        min={0}
                        max={4}
                        step={1}
                        value={activityIndex}
                        onChange={(_, v) => setActivityIndex(v as number)}
                    />
                    <div className="text-sm text-gray-700">
                        <strong>{t(`activity_level_title_${activityIndex}`)}</strong>
                        <p className="text-xs italic">
                            {t(`activity_level_description_${activityIndex}`)}
                        </p>
                    </div>
                </div>

                {/* Line 4 */}
                <div className="mt-6">
                    <FormControl fullWidth>
                        <InputLabel id="goal-label">{t('goal')}</InputLabel>
                        <Select
                            labelId="goal-label"
                            label={t('goal')}
                            value={goal}
                            onChange={(e) => setGoal(e.target.value as GoalKey)}
                        >
                            {goalOptions.map((g) => (
                                <MenuItem key={g} value={g}>
                                    {t(`goal-option_${g}`)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>

                {/* Line 5 */}
                <div className="mt-6 flex">
                    <div className="flex-grow">
                        <FormControl fullWidth>
                            <InputLabel id="diet-label">{t('diet_type')}</InputLabel>
                            <Select
                                labelId="diet-label"
                                label={t('diet_type')}
                                value={diet}
                                onChange={(e) => setDiet(e.target.value as DietKey)}
                            >
                                {dietOptions.map((d) => (
                                    <MenuItem key={d} value={d}>
                                        {t(`diet-option_${d}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                    <DietTypeInfoPopover dietType={diet} />
                </div>

                {/* Line 6: Results */}
                <div className="mt-4 px-4 py-2 border border-gray-300 rounded">
                    <div className="text-lg text-center font-bold text-gray-600">
                        {t('your_goals')}:
                    </div>
                    <div className="flex flex-wrap mt-4 mb-4 justify-between items-center">
                        <div className="p-2 bg-blue-400 rounded">
                            <div className="text-white text-4xl font-light">
                                {results.calories.toLocaleString()}
                            </div>
                            <div className="text-white text-xs">{t('cal_day')}:</div>
                        </div>

                        <div className="text-xs">
                            <div className="flex items-center pr-4">
                                <MacroBullet type="protein" />
                                <b>{t('protein')}:&nbsp;</b> {results.protein} {t('gr')}
                            </div>
                            <div className="flex items-center pr-4">
                                <MacroBullet type="fat" />
                                <b>{t('fat')}:&nbsp;</b> {results.fats} {t('gr')}
                            </div>
                            <div className="flex items-center pr-4">
                                <MacroBullet type="carb" />
                                <b>{t('carbs')}:&nbsp;</b> {results.carbsNoSugars} {t('gr')}
                            </div>
                            <div className="flex items-center pr-4">
                                <MacroBullet type="sugar" />
                                <b>{t('sugar')}:&nbsp;</b> {results.sugars} {t('gr')}
                            </div>
                        </div>
                    </div>

                    <MacroBar
                        protein={results.protein}
                        fat={results.fats}
                        carb={results.carbsNoSugars}
                        sugar={results.sugars}
                    />
                    <div className="text-xs mt-4 text-green-800">
                        <Trans i18nKey={'you_will_adjust_settings'} />
                    </div>
                </div>

                <div className="text-xs mt-8">
                    <p>
                        <Trans i18nKey={'you_personal_details'} />
                    </p>
                    <p className="my-2">
                        <Trans i18nKey={'we_use_formula'} />
                    </p>
                    <p>
                        <Trans i18nKey={'lifestyle_values'} />
                    </p>
                </div>
            </DialogContent>

            <DialogActions>
                <LoadingButton onClick={onCancel} disabled={loading}>
                    {t('MODAL_cancel')}
                </LoadingButton>
                <LoadingButton
                    onClick={handleSave}
                    loading={loading}
                    variant="contained"
                    color="success"
                    disabled={ageError || weightError || heightError}
                >
                    {t('MODAL_save_goals')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default CalculatorModal;
